// RecordingControls — Floating control bar during recording (Positioned at TOP)
import { useState } from "react";
import { useRecordingStore } from "@/stores/recording-store";
import { motion, AnimatePresence } from "framer-motion";
import {
  Circle,
  Square,
  Pause,
  Play,
  Mic,
  MicOff,
  Camera,
  CameraOff,
  Volume2,
  VolumeX,
  Bookmark,
  Timer,
  ChevronUp,
  ChevronDown,
  Search,
  ShieldCheck,
  SquareDashedMousePointer,
  Trash2,
  Pencil,
  Camera as ScreenshotIcon,
} from "lucide-react";
import { useMagnifierStore } from "@/stores/magnifier-store";
import { usePrivacyStore, PRIVACY_PATTERNS } from "@/stores/privacy-store";

export default function RecordingControls({
  isRecording,
  isPaused,
  recordingTime,
  onStart,
  onPause,
  onResume,
  onStop,
  onScreenshot,
}) {
  const { settings, setSettings, addMarker, markers, countdown, setCountdown } = useRecordingStore();
  const magnifier = useMagnifierStore();
  const privacy = usePrivacyStore();
  const [expanded, setExpanded] = useState(false);

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
    return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  };

  return (
    <motion.div
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="fixed top-2 left-1/2 -translate-x-1/2 z-[9999]"
    >
      <div className="rounded-2xl bg-[#1a1a2e]/95 backdrop-blur-xl border border-white/10 shadow-2xl shadow-black/40 overflow-hidden">
        {/* Main controls bar (Top) */}
        <div className="flex items-center gap-2.5 px-4 py-2">
          {/* Recording status */}
          {isRecording && (
            <div className="flex items-center gap-2 mr-1">
              <div className={`w-2.5 h-2.5 rounded-full ${isPaused ? "bg-amber-400" : "bg-red-500 animate-pulse"}`} />
              <span className="text-white font-mono text-sm font-bold tracking-wider">
                {formatTime(recordingTime)}
              </span>
              {isPaused && (
                <span className="text-[9px] text-amber-400 font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-400/10 border border-amber-400/20">
                  Pause
                </span>
              )}
            </div>
          )}

          {/* Divider */}
          {isRecording && <div className="w-px h-6 bg-white/10" />}

          {/* Media toggles */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => setSettings({ includeMic: !settings.includeMic })}
              className={`p-1.5 rounded-lg transition-colors ${
                settings.includeMic ? "bg-emerald-500/20 text-emerald-400" : "bg-white/5 text-white/30 hover:text-white/50"
              }`}
              title={settings.includeMic ? "Micro activé" : "Micro désactivé"}
            >
              {settings.includeMic ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
            </button>
            <button
              onClick={() => setSettings({ includeWebcam: !settings.includeWebcam })}
              className={`p-1.5 rounded-lg transition-colors ${
                settings.includeWebcam ? "bg-blue-500/20 text-blue-400" : "bg-white/5 text-white/30 hover:text-white/50"
              }`}
              title={settings.includeWebcam ? "Webcam activée" : "Webcam désactivée"}
            >
              {settings.includeWebcam ? <Camera className="h-4 w-4" /> : <CameraOff className="h-4 w-4" />}
            </button>
            <button
              onClick={() => magnifier.setEnabled(!magnifier.isEnabled)}
              className={`p-1.5 rounded-lg transition-colors ${
                magnifier.isEnabled ? "bg-amber-500/20 text-amber-400" : "bg-white/5 text-white/30 hover:text-white/50"
              }`}
              title={magnifier.isEnabled ? "Désactiver la loupe" : "Activer la loupe"}
            >
              <Search className="h-4 w-4" />
            </button>
            <button
              onClick={() => {
                const next = !privacy.isEnabled;
                privacy.setEnabled(next);
                if (next) setExpanded(true);
                else {
                  privacy.setDrawing(false);
                  privacy.setEditingZones(false);
                }
              }}
              className={`p-1.5 rounded-lg transition-colors ${
                privacy.isEnabled ? "bg-rose-500/20 text-rose-400" : "bg-white/5 text-white/30 hover:text-white/50"
              }`}
              title={privacy.isEnabled ? "Confidentialité active — données sensibles masquées" : "Masquer les données sensibles"}
            >
              <ShieldCheck className="h-4 w-4" />
            </button>
            <button
              onClick={() => setSettings({ includeSystemAudio: !settings.includeSystemAudio })}
              className={`p-1.5 rounded-lg transition-colors ${
                settings.includeSystemAudio ? "bg-purple-500/20 text-purple-400" : "bg-white/5 text-white/30 hover:text-white/50"
              }`}
              title={settings.includeSystemAudio ? "Audio système activé" : "Audio système désactivé"}
            >
              {settings.includeSystemAudio ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
            </button>

            {/* 📸 Screenshot button */}
            {onScreenshot && (
              <button
                onClick={onScreenshot}
                className="p-1.5 rounded-lg bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 transition-colors"
                title="Capture d'écran (PNG)"
              >
                <ScreenshotIcon className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="w-px h-6 bg-white/10" />

          {/* Record / Pause / Stop */}
          {!isRecording ? (
            <button
              onClick={onStart}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-xl text-xs font-semibold text-white transition-all hover:shadow-lg"
              style={{
                background: "linear-gradient(135deg, #ef4444, #dc2626)",
                boxShadow: "0 4px 16px rgba(239, 68, 68, 0.3)",
              }}
              data-testid="start-recording"
            >
              <Circle className="h-3.5 w-3.5 fill-white" />
              Démarrer
            </button>
          ) : (
            <>
              <button
                onClick={isPaused ? onResume : onPause}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium bg-white/10 hover:bg-white/15 text-white transition-colors"
              >
                {isPaused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
                {isPaused ? "Reprendre" : "Pause"}
              </button>
              <button
                onClick={onStop}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium text-white transition-all"
                style={{ background: "linear-gradient(135deg, #ef4444, #dc2626)" }}
                data-testid="stop-recording"
              >
                <Square className="h-3.5 w-3.5 fill-white" />
                Arrêter
              </button>
            </>
          )}

          <div className="w-px h-6 bg-white/10" />

          {/* Marker button */}
          {isRecording && (
            <button
              onClick={() => addMarker()}
              className="p-1.5 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 transition-colors"
              title="Ajouter un marqueur"
            >
              <Bookmark className="h-4 w-4" />
            </button>
          )}

          {/* Settings toggle */}
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/40 hover:text-white/60 transition-colors"
            title="Options avancées"
          >
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>

        {/* Expanded settings (Drops downwards) */}
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="border-t border-white/5 overflow-hidden"
            >
              <div className="px-5 py-3 space-y-3">
                {/* Resolution & FPS */}
                <div className="flex items-center gap-4 text-[11px]">
                  <span className="text-white/40">Résolution:</span>
                  <span className="text-white/70 font-mono">{settings.resolution}</span>
                  <span className="text-white/40">FPS:</span>
                  <span className="text-white/70 font-mono">{settings.fps}</span>
                  <span className="text-white/40">Bitrate:</span>
                  <span className="text-white/70 font-mono">{settings.bitrate} Kbps</span>
                </div>

                {/* Countdown */}
                <div className="flex items-center gap-3 text-[11px]">
                  <Timer className="h-3.5 w-3.5 text-white/40" />
                  <span className="text-white/50">Countdown:</span>
                  {[0, 3, 5, 10].map((v) => (
                    <button
                      key={v}
                      onClick={() => setCountdown(v)}
                      className={`px-2 py-0.5 rounded text-[10px] font-medium ${
                        countdown === v ? "bg-primary/30 text-primary" : "bg-white/5 text-white/50 hover:bg-white/10"
                      }`}
                    >
                      {v === 0 ? "Off" : `${v}s`}
                    </button>
                  ))}
                </div>

                {/* Privacy */}
                {privacy.isEnabled && (
                  <div className="space-y-2 pt-1 border-t border-white/5">
                    <div className="flex items-center gap-2 text-[11px]">
                      <ShieldCheck className="h-3.5 w-3.5 text-rose-400" />
                      <span className="text-white/70 font-medium">Confidentialité</span>
                      <span className="text-white/30">— masqué à l'écran et dans la vidéo</span>
                    </div>

                    {/* Pattern toggles */}
                    <div className="flex items-center gap-1 flex-wrap">
                      {Object.entries(PRIVACY_PATTERNS).map(([key, def]) => (
                        <button
                          key={key}
                          onClick={() => privacy.togglePattern(key)}
                          className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
                            privacy.autoPatterns[key]
                              ? "bg-rose-500/20 text-rose-300 border border-rose-500/30"
                              : "bg-white/5 text-white/40 border border-transparent hover:text-white/60"
                          }`}
                        >
                          {def.label}
                        </button>
                      ))}
                    </div>
                    <p className="text-[9px] text-white/25 leading-relaxed">
                      La détection auto ne lit pas le PDF, les slides ni le navigateur — utilisez une zone manuelle.
                    </p>

                    {/* Style + zones */}
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] text-white/40">Style:</span>
                        {[
                          { v: "solid", l: "Opaque" },
                          { v: "blur", l: "Flou" },
                        ].map((o) => (
                          <button
                            key={o.v}
                            onClick={() => privacy.setStyle(o.v)}
                            className={`px-2 py-0.5 rounded text-[10px] font-medium ${
                              privacy.style === o.v ? "bg-white/15 text-white" : "bg-white/5 text-white/40"
                            }`}
                            title={o.v === "blur" ? "Moins sûr : un texte flouté peut être reconstitué" : "Recommandé"}
                          >
                            {o.l}
                          </button>
                        ))}
                      </div>

                      <button
                        onClick={() => privacy.setDrawing(!privacy.drawing)}
                        className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${
                          privacy.drawing ? "bg-rose-500 text-white" : "bg-white/5 text-white/50 hover:text-white/70"
                        }`}
                      >
                        <SquareDashedMousePointer className="h-3 w-3" />
                        {privacy.drawing ? "Dessinez la zone…" : "Ajouter une zone"}
                      </button>

                      {privacy.zones.length > 0 && (
                        <>
                          <button
                            onClick={() => privacy.setEditingZones(!privacy.editingZones)}
                            className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${
                              privacy.editingZones ? "bg-white/15 text-white" : "bg-white/5 text-white/50 hover:text-white/70"
                            }`}
                          >
                            <Pencil className="h-3 w-3" />
                            {privacy.zones.length} zone{privacy.zones.length > 1 ? "s" : ""}
                          </button>
                          <button
                            onClick={() => privacy.clearZones()}
                            className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-white/5 text-white/40 hover:text-rose-400"
                          >
                            <Trash2 className="h-3 w-3" /> Effacer
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Markers */}
                {markers.length > 0 && (
                  <div className="flex items-center gap-2 text-[10px]">
                    <Bookmark className="h-3 w-3 text-amber-400" />
                    <span className="text-white/40">Marqueurs:</span>
                    <div className="flex gap-1 flex-wrap">
                      {markers.map((m) => (
                        <span key={m.id} className="px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400">
                          {formatTime(m.time)} — {m.label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
